import type { ClientPositionView } from "@solomon-apps/shared";

import type { DiscoveredPosition, DiscoveredSquadsDetail } from "@/services/positionDiscovery";

import type { PositionSourceDetection } from "./positionSourceDetection";
import { hasExistingPositionForSource } from "./positionSourcePayload";

/** The flow's client-side classification of the pasted source. Kept for the
 *  verify ceremony (decision 17) and as the Find gate; the discovery endpoint
 *  re-classifies authoritatively server-side. */
export interface ClassifiedPositionSource {
    classification: "eoa" | "multisig-squads";
    /** Where the USDv actually sits: the wallet for EOAs, the vault for
     *  Squads sources (falling back to the multisig account when the vault
     *  is unknown). */
    holdingAddress: string;
    squads: DiscoveredSquadsDetail | null;
}

/**
 * Classify the pasted source from the live detection state. Null while the
 * source is unresolved (empty, invalid, unsupported, or the Squads preflight
 * is still running) — the Find action must not fire yet.
 */
export function classifyPositionSource(
    detection: PositionSourceDetection,
    connectedWalletAddress: string
): ClassifiedPositionSource | null {
    if (detection.status === "eoa") {
        const holdingAddress = detection.address || connectedWalletAddress;
        if (holdingAddress.length === 0) return null;
        return { classification: "eoa", holdingAddress, squads: null };
    }
    if (detection.status === "multisig-squads") {
        const multisig = detection.multisig;
        return {
            classification: "multisig-squads",
            holdingAddress: multisig.vaultAddress ?? multisig.address,
            squads: {
                multisigAddress: multisig.address,
                vaultAddress: multisig.vaultAddress,
                threshold: multisig.threshold,
                memberCount: multisig.memberCount
            }
        };
    }
    return null;
}

/** What the Find step should do with a discovery response. */
export type FindOutcome =
    | { kind: "not-covered" }
    | { kind: "already-registered" }
    | { kind: "found"; holding: DiscoveredPosition; holdings: DiscoveredPosition[]; programSurfaceUuid: string | null };

/** Discover holdings before verification. Program coverage does not gate wallet registration. */
export function resolveFindOutcome(args: {
    holdings: DiscoveredPosition[];
    existingPositions: ClientPositionView[];
    /** The address the API keys the position on: the EOA wallet, or the
     *  multisig account (not the vault) for Squads sources. */
    candidateSourceAddress: string | null;
}): FindOutcome {
    const holdings = args.holdings.filter(
        (candidate) => candidate.surfaceTypeSlug === "wallet-account" || candidate.match.kind === "matched"
    );
    const holding = holdings.find((candidate) => candidate.surfaceTypeSlug === "wallet-account") ?? null;
    if (!holding) {
        return { kind: "not-covered" };
    }
    if (
        holding.match.kind === "matched" &&
        args.candidateSourceAddress !== null &&
        hasExistingPositionForSource(
            args.existingPositions,
            holding.match.programSurfaceUuid,
            args.candidateSourceAddress
        )
    ) {
        return { kind: "already-registered" };
    }
    return {
        kind: "found",
        holding,
        holdings,
        programSurfaceUuid: holding.match.kind === "matched" ? holding.match.programSurfaceUuid : null
    };
}
